/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useMemo, useState } from "react"
import { PlayerButton } from "./PlayerButton"
import {
  getBufferedPercentage,
  getCurrentTime,
  getDuration,
  timeAsPercentage,
  togglePlay,
  IShakaPlayer,
} from "../utils/shaka"
import { RangeInput } from "./RangeInput"
import { useVideoPosition } from "../hooks/usePlayerPosition"
import { PlayerTimeDisplay } from "./PlayerTimeDisplay"
import { PlayerMenu } from "./PlayerMenu"
import { useVolume } from "../hooks/useVolume"
import { useQualitySettings } from "../hooks/useQualitySettings"
import { useTextTrack } from "../hooks/useTextTrack"
import { IVideoData } from "../hooks/useVideoData"
import { useFullScreen } from "../hooks/useFullScreen"
import useDimensions from "react-cool-dimensions"
import { ToolTip } from "./ToolTip"
import { useOnClickOutside } from "../hooks/useOnClickOutside"

interface IProps {
  isReady: boolean
  isPlaying: boolean
  player: IShakaPlayer
  displayToolTips: boolean
  displayOverlay: boolean
  data: IVideoData
  containerRef: React.RefObject<HTMLDivElement>
  videoRef: React.RefObject<HTMLVideoElement>
  captionFns: ReturnType<typeof useTextTrack>
  displayCaptionControl: boolean
}
export function Controls({
  isReady,
  isPlaying,
  player,
  displayToolTips,
  displayOverlay,
  data,
  containerRef,
  videoRef,
  captionFns,
  displayCaptionControl,
}: IProps) {
  const VOLUME_AS_POPOUT = 600
  const TIME_NOT_INLINE = 450
  const SMALLER_PADDING = 550

  const [currentPlayPosition, handleSeek] = useVideoPosition(player)
  const [volume, setVolume] = useVolume(player)
  const [cachedVolume, setCachedVolume] = useState<number | null>(null)
  const { currentQuality, setQuality, availableQualities } = useQualitySettings(player, data)
  const [activeMenu, setActiveMenu] = useState<"quality" | "captions" | "volume" | null>(null)
  const { isFullScreen, handleFullScreen, handleExitFullScreen } = useFullScreen(
    containerRef,
    videoRef,
  )
  useOnClickOutside(containerRef, () => {
    setActiveMenu(null)
  })
  const { observe, width } = useDimensions<HTMLDivElement | null>({
    onResize: ({}) => {
      // Triggered whenever the size of the target is changed...
    },
  })

  const playBtn = useMemo(
    () => (
      <PlayerButton
        onClick={() => togglePlay(player)}
        icon={isPlaying ? "pause" : "play"}
        tooltip={{ text: isPlaying ? "Pause" : "Play", placement: "top-start" }}
        displayToolTips={displayToolTips}
      />
    ),
    [isPlaying],
  )

  const volumeBtn = useMemo(
    () => (
      <PlayerButton
        onClick={() => handleVolumeButtonClick()}
        icon={volume > 0 ? "speaker" : "speaker-muted"}
        tooltip={{ text: volume > 0 ? "Mute" : "Unmute", placement: "top" }}
        displayToolTips={displayToolTips}
      />
    ),
    [volume],
  )

  const volumeControls = useMemo(
    () => (
      <div style={{ display: "flex", alignItems: "center", minWidth: 130 }}>
        {volumeBtn}
        <div
          css={css`
            min-width: 3px;
            flex-shrink: 0;
          `}
        />

        <RangeInput
          value={volume}
          onChange={value => setVolume(value)}
          css={css`
            max-width: 110px;
          `}
        />
      </div>
    ),
    [volume],
  )

  const qualityMenu = useMemo(
    () => (
      <PlayerMenu
        tooltip={{ text: "Quality", placement: "top" }}
        button="settings"
        items={availableQualities}
        value={currentQuality}
        onSelect={value => setQuality(value as number | "auto")}
        open={activeMenu === "quality"}
        setOpen={value => setActiveMenu(!value ? null : "quality")}
        displayToolTips={displayToolTips}
      />
    ),
    [currentQuality, activeMenu],
  )

  const captionsMenu = useMemo(
    () => (
      <PlayerMenu
        tooltip={{ text: "Captions", placement: "top" }}
        button={!captionFns.currentCaptionId ? "captions-off" : "captions"}
        items={[
          ...captionFns.availableCaptions.map(caption => ({
            value: caption.id,
            label: caption.lang,
          })),
          { label: "Off", value: -1 },
        ]}
        value={captionFns.currentCaptionId ?? -1}
        onSelect={value =>
          captionFns.handleSetCurrentCaptionId(value === -1 ? undefined : (value as string))
        }
        open={activeMenu === "captions"}
        setOpen={value => setActiveMenu(!value ? null : "captions")}
        displayToolTips={displayToolTips}
      />
    ),
    [captionFns.currentCaptionId, activeMenu],
  )

  const fullScreenButton = useMemo(
    () => (
      <PlayerButton
        onClick={isFullScreen ? handleExitFullScreen : handleFullScreen}
        icon={isFullScreen ? "exit-fullscreen" : "fullscreen"}
        tooltip={{
          text: isFullScreen ? "Exit fullscreen" : "Fullscreen",
          placement: "top-end",
        }}
        displayToolTips={displayToolTips}
      />
    ),
    [isFullScreen],
  )

  if (!isReady) {
    return null
  }

  function handleVolumeButtonClick() {
    if (volume > 0) {
      setCachedVolume(volume)
      setVolume(0)
    } else {
      setVolume(cachedVolume && cachedVolume > 5 ? cachedVolume : 50)
    }
  }

  return (
    <div ref={observe}>
      <div
        css={css`
          align-items: center;
          display: flex;
          justify-content: flex-end;
          text-align: center;
          background: linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.75));
          border-bottom-left-radius: inherit;
          border-bottom-right-radius: inherit;
          position: absolute;
          bottom: 0;
          left: 0;
          color: white;
          right: 0;
          transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
          z-index: 100;
          padding: ${width < SMALLER_PADDING ? "25px 6px 6px 6px" : "35px 10px 10px 10px"};
          opacity: ${displayOverlay ? 1 : 0};
          transform: translateY(${displayOverlay ? 0 : 10}px);
          pointer-events: none;
          > * {
            pointer-events: all;
          }
          > div {
            min-width: initial;
          }
        `}
        className="fv-player-container-controls"
      >
        {playBtn}
        <div
          css={css`
            min-width: 3px !important;
            flex-shrink: 0;
          `}
        />
        <div
          css={css`
            position: relative;
            width: calc(100% + 13px);
          `}
        >
          <RangeInput value={currentPlayPosition} onChange={handleSeek} />
          {getBufferedPercentage(player).map((info, i) => (
            <div
              key={i}
              css={css`
                height: 5px;
                background: rgba(255, 255, 255, 0.25);
                position: absolute;
                left: ${timeAsPercentage(player, info.start)}%;
                right: ${100 - timeAsPercentage(player, info.end)}%;
                top: 4px;
                border-radius: 5px;
              `}
            />
          ))}
        </div>
        <PlayerTimeDisplay
          lengthInSeconds={getDuration(player)}
          timeElapsedInSeconds={getCurrentTime(player)}
          css={
            width < TIME_NOT_INLINE
              ? css`
                  font-size: 11px;
                  position: absolute;
                  left: 32px;
                  bottom: 30px;
                  font-weight: 600;
                  text-shadow: 0 0 2px black;
                `
              : undefined
          }
        />
        {width < VOLUME_AS_POPOUT ? (
          <React.Fragment>
            <div
              css={css`
                min-width: 6px !important;
                flex-shrink: 0;
              `}
            />
            <VolumePopout
              tooltip={{ text: "Volume", placement: "top" }}
              button="speaker"
              open={activeMenu === "volume"}
              setOpen={value => setActiveMenu(!value ? null : "volume")}
            >
              {volumeControls}
            </VolumePopout>
          </React.Fragment>
        ) : (
          <React.Fragment>
            {volumeControls}
            <div
              css={css`
                min-width: 3px !important;
                flex-shrink: 0;
              `}
            />
          </React.Fragment>
        )}

        {qualityMenu}
        {captionFns.availableCaptions.length > 0 && displayCaptionControl && captionsMenu}
        {fullScreenButton}
      </div>
    </div>
  )
}

interface IVolumePopoutProps {
  tooltip: {
    text: string
    placement: "top" | "top-start" | "top-end"
  }
  button: "speaker"
  open?: boolean
  setOpen(open: boolean): void
  children: React.ReactNode
}
export function VolumePopout({ tooltip, button, open, setOpen, children }: IVolumePopoutProps) {
  return (
    <ToolTip content={<div>{children}</div>} placement="top" interactive>
      <div>
        <PlayerButton
          onClick={() => setOpen(!open)}
          icon={button}
          tooltip={tooltip}
          displayToolTips={false}
        />
      </div>
    </ToolTip>
  )
}
